// @ts-check
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Link, Table, TableBody, Text } from '~/design-system';
import { useCommonLinks } from '../../../pages/imports/_config';
import Notification from '../../Notification';
import DefaultTableHeader from '../DefaultTableHeader';
import ImportsOverviewTableBodySkeleton from '../ImportsOverviewTableBodySkeleton';
import CompletedImportRow from './CompletedImportRow';

export default function ImportsCompleted() {
  const { loading, completedImports } = useImportsCompleted();
  /** @type {[import('../../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const link = useCommonLinks();

  return (
    <React.Fragment>
      <Box sx={{ width: '100%', margin: '0 auto', paddingBottom: 7 }}>
        {!loading && completedImports.length === 0 ? (
          <Text sx={{ marginTop: 4 }}>
            You didn't complete any imports yet.{' '}
            <Link to={link.importsInProgress()}>Show imports in progress</Link>.
          </Text>
        ) : (
          <Table
            sx={{
              border: '1px solid',
              borderColor: 'brand.lightGray',
              width: '100%',
            }}
          >
            <DefaultTableHeader />
            {loading ? (
              <ImportsOverviewTableBodySkeleton />
            ) : (
              <TableBody>
                {completedImports?.map((oneImport, i) => (
                  <CompletedImportRow key={i} oneImport={oneImport} />
                ))}
              </TableBody>
            )}
          </Table>
        )}
      </Box>

      {notification && (
        <Notification
          onClose={() => setNotification(undefined)}
          message={notification.message}
          // aLinkData={notification.aLinkData}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

export function useImportsCompleted() {
  const { workspaceId } = useParams();
  const { data, loading } = useQuery(query, { variables: { workspaceId } });
  const completedImports = (
    data?.workspace?.dataImports?.nodes?.filter(Boolean) || []
  ).sort((a, b) => {
    return new Date(b?.createdAt) - new Date(a?.createdAt);
  });

  return {
    loading,
    completedImports,
  };
}

const query = gql`
  query CompletedImportsPage($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImports(filter: { state: { eq: "COMPLETED" } }) {
        nodes {
          ...ImportOverview
          ...PastImports
          ...CompletedImportRow
          outputUrl
        }
      }
    }
  }

  fragment PastImports on DataImport {
    results {
      object
      addedCount
      updatedCount
      deletedCount
      unchangedCount
    }
  }

  fragment ImportOverview on DataImport {
    importId
    name
    creator {
      fullName
      email
    }
    importOptions
    source {
      ... on DataImportSourceTabular {
        url
      }
    }
    state {
      state
    }
    type
    createdAt
  }
  ${CompletedImportRow.fragment}
`;
